<template>
	<main
		id="app"
		class="theme-dark"
		v-bind:page="$route.name"
		v-bind:data-authenticated="$store.getters.authenticated"
		v-bind:class="featureClasses"
	>
		<ChalkyBrandingSplash v-if="showSplashScreen" v-bind:fullscreen="true" />

		<!-- Primary Layout -->
		<LayoutStandard />

		<!-- Global Alert -->
		<ChalkyUtilityAlertPrompt ref="alertPrompt" />

		<!-- Global Authentication -->
		<ChalkyAuthenticationModal
			v-bind:contextualSignup="false"
			v-bind:includeBasic="$feature.all(['auth_login', 'auth_signup'])"
			v-bind:includeSocial="$feature.all(['auth_social', 'auth_login'])"
			v-bind:model="$store.getters['authentication/authModel']"
			v-if="$feature('auth')"
		/>

		<!-- Global Action Modals -->
		<ViewModalGlobalAddPhoto />
		<ViewModalGlobalAddVideo />
		<ViewModalGlobalCheckin />
		<ViewModalGlobalCreate />
		<!-- <ViewModalGlobalPlay /> -->
		<ViewModalGlobalSubmit />

		<!-- Global Coach Marks -->
		<ViewFeatureAnnouncements />
	</main>
</template>

<script lang="ts">
	import * as Config from '@/Config';
	import * as Manager from '@/Manager';
	import ChalkySticks from '@chalkysticks/sdk';
	import { Component, Vue } from 'vue-property-decorator';
	import { activated, minVersion, mounted } from '@/Utility/Decorators';

	/**
	 * Root application component. Acts as a thin shell that delegates
	 * initialization and business logic to the Manager layer.
	 *
	 * @author Matt Kenefick <matt.kenefick@buck.co>
	 * @package App
	 * @project ChalkySticks
	 */
	@Component
	export default class App extends Vue {
		/**
		 * @return Record<string, boolean>
		 */
		protected get featureClasses(): Record<string, boolean> {
			return {
				'authentication-enabled': this.$feature('auth'),
				'checkins-enabled': this.$feature('venue_checkin'),
				'comments-enabled': this.$feature('comments'),
				'is-android': ChalkySticks.Utility.Device.isAndroid(),
				'is-app': Config.Environment.IS_APP,
				'is-ios': ChalkySticks.Utility.Device.isIOS(),
				'login-enabled': this.$feature('auth_login'),
				'reactions-enabled': this.$feature('reactions'),
				'show-global-menu': this.$store.getters.showPrimaryContextMenu,
				'signup-enabled': this.$feature('auth_signup'),
				'state-keyboard': this.$store.state.global.showingKeyboard,
				'wallet-enabled': this.$feature('wallet'),
			};
		}

		/**
		 * @type boolean
		 */
		protected showSplashScreen: boolean = this.$store.getters.shouldShowSplash;

		/**
		 * @return void
		 */
		public created(): void {
			Manager.App.initialize();
		}

		/**
		 * @return void
		 */
		public mounted(): void {
			Manager.App.boot(this.$refs.alertPrompt);
		}

		/**
		 * @return Promise<void>
		 */
		@mounted
		@activated
		protected async syncData(): Promise<void> {
			Manager.App.refreshAllData();
		}

		/**
		 * @return void
		 */
		@mounted
		@minVersion('>=3.27.0')
		protected testMinimumVersions(): void {
			console.log('Testing minimum versions', Config.App.VERSION);
		}
	}
</script>

<style lang="scss">
	#app {
		height: 100svh;
		overflow: hidden;
		position: fixed;
		width: 100svw;
	}
</style>
